import { useQuery, useQueryClient, UseQueryOptions } from "react-query";
import { useLocation } from "react-router-dom";

import { HTTPError, useKy } from "src/common/ky";
import {
  DealerOrganization,
  DealerUser,
  PropertyOrganization,
} from "src/common/types";

export const DEALER_USER_QUERY_KEY = "DEALER_USER";
export const useDealerUserQuery = (
  userId: string,
  options?: Pick<
    UseQueryOptions<DealerUser>,
    "onSuccess" | "onError" | "onSettled"
  >
) => {
  const ky = useKy();

  return useQuery<DealerUser, HTTPError>(
    [DEALER_USER_QUERY_KEY, userId],
    () => ky.get(`dealer-users/${userId}`).json<DealerUser>(),
    options
  );
};

export const DEALER_USERS_QUERY_KEY = "DEALER_USERS";
export const useDealerUsersQuery = (organizationId: string) => {
  const ky = useKy();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  searchParams.set("dealerOrganizationId", organizationId);

  return useQuery<DealerUser[], HTTPError>(
    [DEALER_USERS_QUERY_KEY, searchParams.toString()],
    () => ky.get("dealer-users", { searchParams }).json<DealerUser[]>()
  );
};

export const DEALER_ORGANIZATIONS_QUERY_KEY = "DEALER_ORGANIZATIONS";
export const useDealerOrganizationQueryData = (organizationId?: string) => {
  const queryClient = useQueryClient();

  if (!organizationId) {
    return null;
  }

  return queryClient.getQueryData<DealerOrganization>(
    `${DEALER_ORGANIZATIONS_QUERY_KEY}:${organizationId}`
  );
};

type DealerPropertiesResponse = {
  pageNumber: number;
  pageSize: number;
  result: PropertyOrganization[];
  total: number;
};

export const DEALER_PROPERTIES_QUERY_KEY = "DEALER_PROPERTIES";
export const useDealerPropertiesQuery = (dealerOrganizationId: string) => {
  const ky = useKy();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  if (!searchParams.has("pageNumber")) {
    searchParams.set("pageNumber", "1");
  }

  searchParams.set("dealerOrganizationId", dealerOrganizationId);
  searchParams.sort();

  return useQuery<DealerPropertiesResponse, HTTPError>(
    [
      DEALER_PROPERTIES_QUERY_KEY,
      dealerOrganizationId,
      searchParams.toString(),
    ],
    () =>
      ky
        .get("property-organizations/search-all", {
          searchParams,
        })
        .json<DealerPropertiesResponse>()
  );
};
